import { useState, useCallback } from "react";
import { useCsvParser, ParserStream } from "./useCsvParser";
import { addBulkUsersMandatoryFields } from "../utils/csvTemplate";
import { ExtendedUserInput } from "../generated/ups/graphql";
import { validate, ValidationError } from "class-validator";
import { plainToInstance } from "class-transformer";
import { UserInput } from "../validations/userCreateValidation";

const LINES_LIMIT = 1000;

type Keys = (typeof addBulkUsersMandatoryFields)[number];

export type DataError = {
  errors: ValidationError[];
  lineNumber: number;
};

export type ParsedUsersType = ExtendedUserInput & { lineNumber: number };

type HeaderIndexes = {
  [K in Keys]: number;
};

const getHeaderIndexes = (headerData: string[]): HeaderIndexes => {
  return addBulkUsersMandatoryFields.reduce((acc, header) => {
    const index = headerData.indexOf(header);
    if (index === -1) {
      throw new Error(`Mandatory Header ${header} not found`);
    }
    return { ...acc, [header]: index };
  }, {}) as HeaderIndexes;
};

const buildUserFromLine = (
  line: {
    data?: string[];
    errors: string[];
    lineNumber: number;
  },
  headerIndexes: HeaderIndexes,
  headers: [number, keyof ExtendedUserInput][],
): { user: ExtendedUserInput; errors: string[] } => {
  const cols = line.data;
  const errors: string[] = [...line.errors];

  if (!cols) {
    return {
      user: {} as ExtendedUserInput,
      errors: [...errors, `No data found in line ${line.lineNumber}`],
    };
  }

  const user: ExtendedUserInput = {} as ExtendedUserInput;
  const { roles, groups, ...rest } = headerIndexes;
  const customAttributes: Record<string, string> = {};

  for (const [index, header] of headers) {
    const columnData = cols[index];
    const isEmpty = isEmptyColumn(columnData);

    if (isEmpty) {
      continue;
    }

    if (index === roles || index === groups) {
      user[header] = columnData.split(",").map((entry) => entry.trim());
      continue;
    }

    if (header in rest) {
      user[header] = columnData;
      continue;
    }

    customAttributes[header] = columnData;
  }

  if (Object.keys(customAttributes).length) {
    user.attributes = customAttributes;
  }

  return { user, errors };
};

const validateUser = async (
  user: ExtendedUserInput,
  additionalValidation?: Array<
    (user: ExtendedUserInput) => ValidationError | null
  >,
) => {
  const userInput = plainToInstance(UserInput, user);
  const errors = await validate(userInput, { stopAtFirstError: true });

  if (additionalValidation?.length) {
    for (const validate of additionalValidation) {
      const additionalErrors = validate(user);
      if (additionalErrors) {
        errors.push(additionalErrors);
      }
    }
  }

  return errors;
};

const isEmptyColumn = (col: string | null | undefined) => {
  return col === null || col === undefined || col.length === 0;
};

const extractInitialStreamData = async (parserStream: ParserStream) => {
  try {
    const [headerRead, firstLineRead] = await Promise.all([
      parserStream[Symbol.asyncIterator]().next(),
      parserStream[Symbol.asyncIterator]().next(),
    ]);
    const headers = headerRead.value as {
      data?: (keyof ExtendedUserInput)[];
      errors: string[];
      lineNumber: number;
    };
    const firstLine = firstLineRead.value;
    if (!headers || !firstLine) {
      throw new Error("The uploaded file contains no user information.");
    }

    const headerData = headers.data;
    if (!headerData) {
      throw new Error("No header data found");
    }

    const headerPairs = Array.from(headerData.entries());
    const headerIndexes = getHeaderIndexes(headerData);
    const { user: firstUser, errors } = buildUserFromLine(
      firstLine,
      headerIndexes,
      headerPairs,
    );

    if (errors.length > 0) {
      return {
        error: { file: errors.map((err) => new Error(err)) },
        headerIndexes: {} as HeaderIndexes,
        firstUser: {} as ParsedUsersType,
        headerPairs: [],
        parserWithHeaderConsumed: parserStream,
      };
    }

    return {
      headerIndexes,
      firstUser: { ...firstUser, lineNumber: firstLine.lineNumber },
      headerPairs,
      parserWithHeaderConsumed: parserStream,
    };
  } catch (error) {
    return {
      error: { file: [error] },
      headerIndexes: {} as HeaderIndexes,
      firstUser: {} as ParsedUsersType,
      headerPairs: [],
      parserWithHeaderConsumed: parserStream,
    };
  }
};

export const useBulkUsersCsvParser = (
  additionalValidation?: Array<
    (user: ExtendedUserInput) => ValidationError | null
  >,
) => {
  const { isParsing, progress, parseIteratorBuilder, reset } = useCsvParser();
  const [isError, setIsError] = useState(false);
  const [fileErrors, setFileErrors] = useState<Error[]>([]);
  const [dataErrors, setDataErrors] = useState<Record<
    string,
    DataError
  > | null>(null);

  const [users, setUsers] = useState<ParsedUsersType[]>([]);
  let stopConsumption: (() => Promise<unknown>) | null = null;

  const resetErrors = useCallback(() => {
    setFileErrors([]);
    setDataErrors(null);
  }, [setFileErrors, setDataErrors]);

  const cancel = useCallback(() => {
    if (stopConsumption) {
      stopConsumption();
    }
    resetErrors();
    setUsers([]);
    reset();
  }, [reset, resetErrors, setUsers, stopConsumption]);

  const consume = async (file: File) => {
    resetErrors();
    setUsers([]);
    const parserStream = parseIteratorBuilder(file);
    if (!stopConsumption) {
      stopConsumption = parserStream[Symbol.asyncIterator]().return;
    }
    const {
      headerIndexes,
      firstUser,
      headerPairs,
      parserWithHeaderConsumed,
      error,
    } = await extractInitialStreamData(parserStream);

    if (error?.file?.length) {
      setFileErrors(error?.file as Error[]);
      setIsError(true);
      return;
    }

    const coveredEmails = new Set<string>([firstUser.email]);
    const usersAcc: ParsedUsersType[] = [firstUser];
    const errorsAcc: Record<string, DataError> = {};

    const allValidations = [...(additionalValidation || [])];

    const firstUserErrors = await validateUser(firstUser, allValidations);

    if (firstUserErrors.length) {
      errorsAcc[firstUser.email] = {
        errors: firstUserErrors,
        lineNumber: 1,
      };
    }

    let lines = 1;
    for await (const line of parserWithHeaderConsumed) {
      if (!line) {
        continue;
      }
      lines++;
      const { user, errors: buildErrors } = buildUserFromLine(
        line,
        headerIndexes,
        headerPairs,
      );

      if (buildErrors.length) {
        setFileErrors((prev) => [
          ...prev,
          ...buildErrors.map((err) => new Error(err)),
        ]);
        continue;
      }

      const currentValidations = [...(additionalValidation || [])];

      const validationErrors = await validateUser(user, currentValidations);

      if (validationErrors.length) {
        errorsAcc[user.email] = {
          errors: validationErrors,
          lineNumber: line.lineNumber,
        };
      }

      if (coveredEmails.has(user.email)) {
        errorsAcc[user.email] = {
          errors: [
            ...(errorsAcc[user.email]?.errors || []),
            {
              property: "email",
              value: user.email,
              target: user,
              constraints: {
                isUnique: `The email ${user.email} already exists`,
              },
            } as ValidationError,
          ],
          lineNumber: line.lineNumber,
        };
      }

      coveredEmails.add(user.email);
      usersAcc.push({ ...user, lineNumber: line.lineNumber });
    }

    if (lines > LINES_LIMIT) {
      setIsError(true);
      setFileErrors((prev) => [
        ...(prev || []),
        new Error(
          `The file size is too large or there are too many users. Please reduce the number of users to ${LINES_LIMIT}.`,
        ),
      ]);
    }

    const isFoundErrors = Object.values(errorsAcc).length > 0;
    setUsers(usersAcc);
    setDataErrors(errorsAcc);
    setIsError(isFoundErrors);
  };

  return {
    isParsing,
    progress,
    users,
    errors: { file: fileErrors, data: Object.values(dataErrors || {}) },
    isError,
    consume,
    cancel,
  };
};
